import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ArrowUp,
  ArrowDown,
  Search,
  Filter,
  AlertCircle,
  Target,
  Crosshair,
  Zap,
  Loader,
  X,
  Check,
  ChevronDown,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  Layers,
  Plus,
  FileText,
  Link2,
  Eye,
  Globe,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import KeywordSparkline from "./KeywordSparkline";
import KeywordDetailModal, { buildPageGeneratorUrl } from "./KeywordDetailModal";

const LOW_CONFIDENCE_THRESHOLD = 5;

const PRIORITY_CONFIG = {
  high: { label: "High", color: "text-red-400", bg: "bg-red-900/20", border: "border-red-800/30" },
  medium: { label: "Medium", color: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-800/30" },
  low: { label: "Low", color: "text-green-400", bg: "bg-green-900/20", border: "border-green-800/30" },
  monitor: { label: "Monitor", color: "text-gray-400", bg: "bg-gray-800/40", border: "border-gray-700/30" },
  ignore: { label: "Ignore", color: "text-gray-600", bg: "bg-gray-900/40", border: "border-gray-800/30" },
};

const TABS = [
  { key: "keywords", label: "Keywords", icon: Search },
  { key: "strategy", label: "Strategy", icon: Target },
];

/* ============== Priority Badge ============== */
function PriorityBadge({ priority, onClick }) {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.monitor;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color} hover:opacity-80 transition-opacity`}
    >
      {config.label}
      <ChevronDown size={10} />
    </button>
  );
}

/* ============== Priority Dropdown ============== */
function PriorityDropdown({ current, onSelect, onClose }) {
  return (
    <div className="absolute z-50 mt-1 w-36 bg-dark-300 border border-dark-50 rounded-lg shadow-xl py-1">
      {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
        <button
          key={key}
          onClick={() => { onSelect(key); onClose(); }}
          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-dark-50/50 transition-colors ${current === key ? config.color + " font-medium" : "text-gray-400"}`}
        >
          {config.label}
        </button>
      ))}
    </div>
  );
}

/* ============== Set Priority Modal ============== */
function SetPriorityModal({ keyword, selectedSite, onClose, onSaved }) {
  const [priority, setPriority] = useState(keyword.priority || "monitor");
  const [targetPos, setTargetPos] = useState(keyword.target_position || "");
  const [targetUrl, setTargetUrl] = useState(keyword.strategy_target_url || "");
  const [group, setGroup] = useState(keyword.keyword_group || "");
  const [notes, setNotes] = useState(keyword.notes || "");
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!selectedSite) return;
    api.get(`/websites/${selectedSite.id}/keyword-groups/`)
      .then((r) => setGroups(r.data || []))
      .catch(() => {});
  }, [selectedSite]);

  const handleSave = () => {
    setSaving(true);
    api.patch(`/websites/${selectedSite.id}/keywords/${keyword.id}/priority/`, {
      priority,
      target_position: targetPos || null,
      strategy_target_url: targetUrl,
      keyword_group: group,
      notes,
    })
      .then((res) => { onSaved(res.data); onClose(); })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Set Priority</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="text-xs text-copper font-mono">{keyword.keyword}</div>

        <div>
          <label className="text-xs text-gray-500 mb-1 block">Priority</label>
          <div className="flex gap-1.5">
            {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
              <button key={key} onClick={() => setPriority(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${priority === key ? config.bg + " " + config.color : "bg-dark text-gray-500 hover:text-gray-300"}`}>
                {config.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Target Position</label>
            <input type="number" min="1" max="100" value={targetPos}
              onChange={(e) => setTargetPos(e.target.value)}
              placeholder="e.g. 3"
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Keyword Group</label>
            <input type="text" value={group}
              onChange={(e) => setGroup(e.target.value)}
              placeholder="e.g. bathroom"
              list="group-suggestions"
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
            <datalist id="group-suggestions">
              {groups.map((g) => <option key={g.group} value={g.group} />)}
            </datalist>
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-500 mb-1 block">Target Page URL</label>
          <input type="url" value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            placeholder="https://..."
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none" />
        </div>

        <div>
          <label className="text-xs text-gray-500 mb-1 block">Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={2} placeholder="Any notes..."
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-copper/50 focus:outline-none resize-none" />
        </div>

        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-copper text-dark rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-copper/90 transition-colors">
            {saving ? "Saving..." : "Save"}
          </button>
          <button onClick={onClose} className="px-4 py-2 text-gray-400 text-sm hover:text-white">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ============== Apply Fix Sub-Modal ============== */
function ApplyFixModal({ keyword, selectedSite, fixType, recAction, onClose, onApplied }) {
  const [currentValue, setCurrentValue] = useState("");
  const [suggestedValue, setSuggestedValue] = useState("");
  const [editValue, setEditValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);

  const fixLabels = {
    title: "Title Tag",
    meta_description: "Meta Description",
    h1: "H1 Tag",
    content: "Additional Content",
  };

  // Fetch current data and generate suggestion
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/websites/${selectedSite.id}/keywords/${keyword.id}/seo-data/`),
    ])
      .then(([seoRes]) => {
        const data = seoRes.data;
        if (fixType === "title") setCurrentValue(data.seo_title || data.current_title || "");
        else if (fixType === "meta_description") setCurrentValue(data.current_meta_description || "");
        else if (fixType === "h1") setCurrentValue(data.current_h1 || "");
        else setCurrentValue(`${data.word_count || 0} words`);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleGenerate = () => {
    setGenerating(true);
    api.post(`/websites/${selectedSite.id}/keywords/${keyword.id}/generate-fix/`, {
      fix_type: fixType,
      current_value: currentValue,
    })
      .then((res) => {
        setSuggestedValue(res.data.suggested || "");
        setEditValue(res.data.suggested || "");
      })
      .catch(() => {})
      .finally(() => setGenerating(false));
  };

  // Auto-generate on load
  useEffect(() => {
    if (!loading && !suggestedValue) {
      handleGenerate();
    }
  }, [loading]);

  const handlePush = () => {
    if (!editValue.trim()) return;
    setPushing(true);
    api.post(`/websites/${selectedSite.id}/keywords/${keyword.id}/apply-fix/`, {
      fix_type: fixType,
      new_value: editValue,
      current_value: currentValue,
    })
      .then((res) => {
        if (res.data.success) {
          setResult("success");
          onApplied(fixType);
        } else {
          setResult(res.data.error || "Failed");
        }
      })
      .catch((err) => {
        setResult(err.response?.data?.error || "Push failed");
      })
      .finally(() => setPushing(false));
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-300 border border-copper/30 rounded-2xl w-full max-w-xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">Apply Fix: {fixLabels[fixType] || fixType}</h4>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>

        <div className="text-xs text-copper font-mono">{keyword.keyword}</div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader size={16} className="animate-spin text-copper" />
            <span className="ml-2 text-gray-400 text-sm">Fetching current data...</span>
          </div>
        ) : result === "success" ? (
          <div className="text-center py-8">
            <CheckCircle size={32} className="text-green-400 mx-auto mb-2" />
            <p className="text-sm text-green-400 font-medium">Fix applied successfully!</p>
            <p className="text-xs text-gray-500 mt-1">Changes pushed to WordPress.</p>
          </div>
        ) : (
          <>
            {/* Current value */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Current {fixLabels[fixType]}</label>
              <div className="bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-400 max-h-24 overflow-y-auto">
                {currentValue || <span className="italic text-gray-600">Empty / not set</span>}
              </div>
            </div>

            {/* Suggested value */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-500">AI-Suggested {fixLabels[fixType]}</label>
                <button onClick={handleGenerate} disabled={generating}
                  className="text-[10px] text-copper hover:underline flex items-center gap-1 disabled:opacity-50">
                  {generating ? <Loader size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                  Regenerate
                </button>
              </div>
              {generating ? (
                <div className="bg-dark border border-dark-50 rounded-lg px-3 py-4 text-center">
                  <Loader size={14} className="animate-spin text-copper mx-auto" />
                  <span className="text-xs text-gray-500 mt-1 block">AI generating suggestion...</span>
                </div>
              ) : fixType === "content" ? (
                <textarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  rows={8}
                  className="w-full bg-dark border border-copper/30 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-copper/50 focus:outline-none resize-y"
                />
              ) : (
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-full bg-dark border border-copper/30 rounded-lg px-3 py-2 text-sm text-white focus:border-copper/50 focus:outline-none"
                />
              )}
              {fixType === "title" && editValue && (
                <div className={`text-[10px] mt-1 ${editValue.length > 60 ? "text-red-400" : "text-gray-500"}`}>
                  {editValue.length}/60 characters
                </div>
              )}
              {fixType === "meta_description" && editValue && (
                <div className={`text-[10px] mt-1 ${editValue.length > 160 ? "text-red-400" : "text-gray-500"}`}>
                  {editValue.length}/160 characters
                </div>
              )}
            </div>

            {result && result !== "success" && (
              <div className="text-xs text-red-400 bg-red-900/20 rounded-lg p-2">Error: {result}</div>
            )}

            <div className="flex gap-2">
              <button onClick={handlePush} disabled={pushing || !editValue.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-copper text-dark rounded-lg text-sm font-medium hover:bg-copper/90 disabled:opacity-50 transition-colors">
                {pushing ? <Loader size={14} className="animate-spin" /> : <Zap size={14} />}
                Push to WordPress
              </button>
              <button onClick={onClose} className="px-4 py-2.5 text-gray-400 text-sm hover:text-white">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ============== Action Plan Modal ============== */
function ActionPlanModal({ keyword, selectedSite, onClose }) {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyFix, setApplyFix] = useState(null);
  const [completedFixes, setCompletedFixes] = useState(new Set());

  useEffect(() => {
    api.get(`/websites/${selectedSite.id}/keywords/${keyword.id}/action-plan/`)
      .then((r) => setPlan(r.data))
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  }, [keyword.id, selectedSite]);

  const readinessColor = (score) => score >= 70 ? "text-green-400" : score >= 40 ? "text-amber-400" : "text-red-400";
  const impactColor = (impact) => impact === "high" ? "text-red-400" : impact === "medium" ? "text-amber-400" : "text-blue-400";
  const impactLabel = (impact) => impact === "high" ? "HIGH" : impact === "medium" ? "MED" : "LOW";

  const getFixType = (rec) => {
    const action = (rec.action || "").toLowerCase();
    if (action.includes("title tag") || action.includes("to the title")) return "title";
    if (action.includes("meta description")) return "meta_description";
    if (action.includes("h1")) return "h1";
    if (action.includes("more words") || action.includes("word count") || action.includes("content")) return "content";
    if (action.includes("internal link")) return "internal_links";
    if (action.includes("backlink")) return "backlinks";
    return null;
  };

  const isFixable = (fixType) => {
    return ["title", "meta_description", "h1", "content"].includes(fixType);
  };

  const handleFixApplied = (fixType) => {
    setCompletedFixes((prev) => new Set([...prev, fixType]));
    // Refresh plan data
    setTimeout(() => {
      api.get(`/websites/${selectedSite.id}/keywords/${keyword.id}/action-plan/`)
        .then((r) => setPlan(r.data))
        .catch(() => {});
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold">Action Plan</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin text-copper" size={20} />
            <span className="ml-2 text-gray-400 text-sm">Loading plan...</span>
          </div>
        ) : !plan ? (
          <div className="text-center py-12 text-gray-500 text-sm">
            No action plan generated yet. Click "Generate Action Plans" on the Strategy tab.
          </div>
        ) : (
          <div className="space-y-5">
            {/* Header */}
            <div>
              <div className="text-copper font-mono text-sm mb-1">{keyword.keyword}</div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span>Position: {keyword.current_position?.toFixed(1) ?? "—"}</span>
                <span>Target: {keyword.target_position ?? "—"}</span>
                {keyword.current_position && keyword.target_position && (
                  <span className="text-red-400">Gap: {(keyword.current_position - keyword.target_position).toFixed(1)}</span>
                )}
              </div>
              {plan.landing_page_url && (
                <div className="text-xs text-gray-600 mt-1 truncate">Page: {plan.landing_page_url}</div>
              )}
            </div>

            {/* Scores */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: "Content", score: plan.content_score },
                { label: "On-Page", score: plan.on_page_score },
                { label: "Authority", score: plan.authority_score },
                { label: "Readiness", score: plan.overall_readiness },
              ].map(({ label, score }) => (
                <div key={label} className="bg-dark rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-500 mb-1">{label}</div>
                  <div className={`text-lg font-mono font-bold ${readinessColor(score)}`}>{score}</div>
                </div>
              ))}
            </div>

            {/* Checklist */}
            <div className="space-y-2">
              <div className="text-xs text-gray-500 font-medium mb-2">SEO Checklist</div>
              {[
                { check: plan.has_landing_page, label: "Dedicated landing page exists" },
                { check: plan.keyword_in_title || completedFixes.has("title"), label: "Keyword in title tag" },
                { check: plan.keyword_in_h1 || completedFixes.has("h1"), label: "Keyword in H1 tag" },
                { check: plan.keyword_in_meta_description || completedFixes.has("meta_description"), label: "Keyword in meta description" },
                { check: plan.keyword_in_url, label: "Keyword in URL" },
              ].map(({ check, label }) => (
                <div key={label} className="flex items-center gap-2 text-xs">
                  {check ? (
                    <CheckCircle size={14} className="text-green-400 flex-shrink-0" />
                  ) : (
                    <XCircle size={14} className="text-red-400 flex-shrink-0" />
                  )}
                  <span className={check ? "text-gray-300" : "text-gray-400"}>{label}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-xs">
                {plan.landing_page_word_count >= 2000 ? (
                  <CheckCircle size={14} className="text-green-400 flex-shrink-0" />
                ) : (
                  <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />
                )}
                <span className="text-gray-400">
                  Content: {plan.landing_page_word_count} words
                  {plan.landing_page_word_count < 2000 && ` (recommended: 2,000+)`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {plan.internal_links_to_page >= 8 ? (
                  <CheckCircle size={14} className="text-green-400 flex-shrink-0" />
                ) : (
                  <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />
                )}
                <span className="text-gray-400">
                  Internal links: {plan.internal_links_to_page} {plan.internal_links_to_page < 8 && `(recommended: 8+)`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {plan.external_backlinks_to_page >= 5 ? (
                  <CheckCircle size={14} className="text-green-400 flex-shrink-0" />
                ) : (
                  <XCircle size={14} className="text-red-400 flex-shrink-0" />
                )}
                <span className="text-gray-400">
                  External backlinks: {plan.external_backlinks_to_page} {plan.external_backlinks_to_page < 5 && `(recommended: 5+)`}
                </span>
              </div>
            </div>

            {/* AI Recommendations with Apply buttons */}
            {plan.recommendations && plan.recommendations.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-gray-500 font-medium mb-2">AI Recommendations</div>
                {plan.recommendations.map((rec, i) => {
                  const fixType = getFixType(rec);
                  const canFix = isFixable(fixType);
                  const isFixed = completedFixes.has(fixType);

                  return (
                    <div key={i} className="flex items-center gap-2 bg-dark rounded-lg p-3">
                      <span className={`text-[10px] font-bold shrink-0 ${impactColor(rec.impact)}`}>
                        {impactLabel(rec.impact)}
                      </span>
                      <span className="text-xs text-gray-300 flex-1">{rec.action}</span>
                      {isFixed ? (
                        <span className="flex items-center gap-1 text-xs text-green-400 shrink-0">
                          <CheckCircle size={12} /> Done
                        </span>
                      ) : canFix ? (
                        <button
                          onClick={() => setApplyFix({ fixType, rec })}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs bg-copper/20 text-copper rounded hover:bg-copper/30 transition-colors shrink-0"
                        >
                          <Zap size={10} /> Apply Fix
                        </button>
                      ) : fixType === "internal_links" ? (
                        <button
                          onClick={() => { onClose(); navigate("/internal-links?tab=suggestions"); }}
                          className="text-[10px] text-copper hover:text-copper/80 shrink-0 hover:underline"
                        >→ Internal Links</button>
                      ) : fixType === "backlinks" ? (
                        <button
                          onClick={() => { onClose(); navigate("/backlinks?tab=directories"); }}
                          className="text-[10px] text-copper hover:text-copper/80 shrink-0 hover:underline"
                        >→ Backlinks</button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Apply Fix sub-modal */}
      {applyFix && (
        <ApplyFixModal
          keyword={keyword}
          selectedSite={selectedSite}
          fixType={applyFix.fixType}
          recAction={applyFix.rec.action}
          onClose={() => setApplyFix(null)}
          onApplied={handleFixApplied}
        />
      )}
    </div>
  );
}

/* ============== Add Strategy Keyword Modal ============== */
function AddStrategyKeywordModal({ selectedSite, groups, onClose, onSaved }) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [priority, setPriority] = useState("high");
  const [targetPos, setTargetPos] = useState(3);
  const [kwGroup, setKwGroup] = useState("");
  const [saving, setSaving] = useState(false);

  // Debounced check
  useEffect(() => {
    if (!keyword.trim() || keyword.trim().length < 3) {
      setCheckResult(null);
      return;
    }
    const timer = setTimeout(() => {
      setChecking(true);
      api.get(`/websites/${selectedSite.id}/keywords/check-pages/`, { params: { keyword: keyword.trim() } })
        .then((r) => setCheckResult(r.data))
        .catch(() => setCheckResult(null))
        .finally(() => setChecking(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [keyword, selectedSite]);

  const readinessScore = useMemo(() => {
    if (!checkResult) return 0;
    let score = 0;
    if (checkResult.found) score += 30;
    if (checkResult.pages?.some((p) => p.has_keyword_in_title)) score += 20;
    if (checkResult.pages?.some((p) => p.has_keyword_in_h1)) score += 20;
    if (checkResult.pages?.some((p) => p.has_keyword_in_meta)) score += 15;
    if (checkResult.pages?.some((p) => p.word_count >= 1500)) score += 15;
    return score;
  }, [checkResult]);

  const handleSave = async () => {
    if (!keyword.trim()) return;
    setSaving(true);
    try {
      const existingKw = checkResult?.existing_keyword;
      if (existingKw) {
        await api.patch(`/websites/${selectedSite.id}/keywords/${existingKw.id}/priority/`, {
          priority,
          target_position: targetPos,
          keyword_group: kwGroup || undefined,
          strategy_target_url: checkResult?.pages?.[0]?.url || "",
        });
      } else {
        // Create keyword then set priority
        const res = await api.post(`/websites/${selectedSite.id}/keywords/`, {
          keyword: keyword.trim(),
          priority,
          target_position: targetPos,
          keyword_group: kwGroup || "",
        });
        if (res.data?.id) {
          await api.patch(`/websites/${selectedSite.id}/keywords/${res.data.id}/priority/`, {
            priority,
            target_position: targetPos,
            keyword_group: kwGroup || undefined,
            strategy_target_url: checkResult?.pages?.[0]?.url || "",
          });
        }
      }
      onSaved();
      onClose();
    } catch {
      // If POST fails (keyword might already exist), try finding by search
      try {
        const searchRes = await api.get(`/websites/${selectedSite.id}/keywords/`, { params: { search: keyword.trim() } });
        const results = searchRes.data.results || searchRes.data;
        const match = results.find((k) => k.keyword.toLowerCase() === keyword.trim().toLowerCase());
        if (match) {
          await api.patch(`/websites/${selectedSite.id}/keywords/${match.id}/priority/`, {
            priority,
            target_position: targetPos,
            keyword_group: kwGroup || undefined,
          });
          onSaved();
          onClose();
        }
      } catch {}
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePage = () => {
    onClose();
    navigate(buildPageGeneratorUrl(keyword));
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Plus size={16} className="text-copper" /> Add Strategy Keyword
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>

        {/* Keyword input */}
        <div className="mb-4">
          <label className="text-xs text-gray-500 mb-1 block">Keyword you want to rank for</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g. dental office construction NYC"
              autoFocus
              className="w-full bg-dark border border-dark-50 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-copper/50" />
            {checking && <Loader size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-copper animate-spin" />}
          </div>
        </div>

        {/* Page analysis results */}
        {checkResult && (
          <div className="mb-4 bg-dark rounded-lg p-3 space-y-2">
            {checkResult.found ? (
              <>
                <div className="flex items-center gap-2 text-xs">
                  <CheckCircle size={14} className="text-green-400" />
                  <span className="text-green-400 font-medium">Page found targeting this keyword</span>
                </div>
                {checkResult.pages.map((p, i) => (
                  <div key={i} className="bg-dark-300 rounded-lg p-2.5 text-xs space-y-1">
                    <div className="text-gray-300 truncate font-mono">{p.url}</div>
                    <div className="flex flex-wrap gap-2">
                      {p.has_keyword_in_title
                        ? <span className="text-green-400">Title</span>
                        : <span className="text-red-400">Title</span>}
                      {p.has_keyword_in_h1
                        ? <span className="text-green-400">H1</span>
                        : <span className="text-red-400">H1</span>}
                      {p.has_keyword_in_meta
                        ? <span className="text-green-400">Meta</span>
                        : <span className="text-red-400">Meta</span>}
                      <span className="text-gray-500">{p.word_count} words</span>
                    </div>
                  </div>
                ))}
                {/* Readiness score */}
                <div className="flex items-center gap-2 text-xs mt-1">
                  <span className="text-gray-500">Readiness:</span>
                  <span className={`font-mono font-bold ${readinessScore >= 70 ? "text-green-400" : readinessScore >= 40 ? "text-amber-400" : "text-red-400"}`}>
                    {readinessScore}/100
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-xs">
                  <XCircle size={14} className="text-amber-400" />
                  <span className="text-amber-400">No page targets this keyword yet</span>
                </div>
                <button onClick={handleCreatePage}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs bg-green-900/20 text-green-400 rounded-lg hover:bg-green-900/30 transition-colors w-full justify-center mt-1">
                  <Plus size={12} /> Create Landing Page
                </button>
              </>
            )}
            {checkResult.existing_keyword && (
              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                <Eye size={12} /> Already tracked — position: {checkResult.existing_keyword.current_position?.toFixed(1) ?? "—"}
                {checkResult.existing_keyword.priority !== "ignore" && ` · ${checkResult.existing_keyword.priority} priority`}
              </div>
            )}
            {checkResult.competitor_count > 0 && (
              <div className="text-xs text-amber-400 flex items-center gap-1">
                <Globe size={12} /> {checkResult.competitor_count} competitor(s) rank for this keyword
              </div>
            )}
          </div>
        )}

        {/* Strategy settings */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Target Position</label>
            <input type="number" min={1} max={10} value={targetPos} onChange={(e) => setTargetPos(Number(e.target.value))}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Keyword Group</label>
            <input type="text" value={kwGroup} onChange={(e) => setKwGroup(e.target.value)}
              list="kw-groups" placeholder="e.g. dental"
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50" />
            <datalist id="kw-groups">
              {groups.map((g) => <option key={g.group} value={g.group} />)}
            </datalist>
          </div>
        </div>

        {/* Priority */}
        <div className="mb-5">
          <label className="text-xs text-gray-500 mb-1.5 block">Priority</label>
          <div className="flex gap-2">
            {["high", "medium", "low"].map((p) => {
              const cfg = PRIORITY_CONFIG[p];
              return (
                <button key={p} onClick={() => setPriority(p)}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors border ${
                    priority === p ? `${cfg.bg} ${cfg.color} ${cfg.border}` : "border-dark-50 text-gray-500 hover:text-gray-300"
                  }`}>
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving || !keyword.trim()}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-copper text-dark rounded-lg text-sm font-medium hover:bg-copper/90 disabled:opacity-50 transition-colors">
            {saving ? <Loader size={14} className="animate-spin" /> : <Target size={14} />}
            {saving ? "Saving..." : "Add to Strategy"}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 text-gray-400 text-sm hover:text-white">Cancel</button>
        </div>
      </div>
    </div>
  );
}


/* ============== Strategy Tab ============== */
function StrategyTab({ selectedSite, keywords, onKeywordUpdate }) {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [planKeyword, setPlanKeyword] = useState(null);
  const [groupFilter, setGroupFilter] = useState("all");
  const [cardFilter, setCardFilter] = useState(null); // "high" | "medium" | "striking" | "no_page"
  const [showAddModal, setShowAddModal] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);

  const fetchData = useCallback(() => {
    if (!selectedSite) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${selectedSite.id}/keyword-strategy/`),
      api.get(`/websites/${selectedSite.id}/keyword-strategy/gaps/`),
      api.get(`/websites/${selectedSite.id}/keyword-groups/`),
    ])
      .then(([overviewRes, gapsRes, groupsRes]) => {
        setOverview(overviewRes.data);
        setGaps(gapsRes.data || []);
        setGroups(groupsRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSite]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAnalyze = () => {
    setAnalyzing(true);
    api.post(`/websites/${selectedSite.id}/keyword-strategy/analyze/`)
      .then(() => {
        const poll = setInterval(() => {
          api.get(`/websites/${selectedSite.id}/keyword-strategy/`)
            .then((r) => {
              setOverview(r.data);
              if (r.data.total_action_plans > 0) {
                clearInterval(poll);
                setAnalyzing(false);
                fetchData();
              }
            })
            .catch(() => {});
        }, 5000);
        setTimeout(() => { clearInterval(poll); setAnalyzing(false); fetchData(); }, 120000);
      })
      .catch(() => setAnalyzing(false));
  };

  const filteredGaps = useMemo(() => {
    let data = [...gaps];
    if (groupFilter !== "all") {
      data = data.filter((g) => g.keyword_group === groupFilter);
    }
    if (cardFilter === "high") {
      data = data.filter((g) => g.priority === "high");
    } else if (cardFilter === "medium") {
      data = data.filter((g) => g.priority === "medium");
    } else if (cardFilter === "striking") {
      data = data.filter((g) => g.position >= 11 && g.position <= 20);
    } else if (cardFilter === "no_page") {
      data = data.filter((g) => g.action_plan && !g.action_plan.has_landing_page);
    }
    return data;
  }, [gaps, groupFilter, cardFilter]);

  const readinessColor = (score) => {
    if (score >= 70) return "text-green-400";
    if (score >= 40) return "text-amber-400";
    return "text-red-400";
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filteredGaps.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredGaps.map((g) => g.id)));
    }
  };

  const handleBulkCreatePages = () => {
    const noPage = filteredGaps.filter((g) => selected.has(g.id) && g.action_plan && !g.action_plan.has_landing_page);
    if (noPage.length === 0) return;
    // Navigate to first keyword's page generator
    navigate(buildPageGeneratorUrl(noPage[0].keyword));
    setBulkAction(null);
    setSelected(new Set());
  };

  const handleBulkGeneratePlans = () => {
    setAnalyzing(true);
    setBulkAction(null);
    setSelected(new Set());
    api.post(`/websites/${selectedSite.id}/keyword-strategy/analyze/`)
      .then(() => {
        const poll = setInterval(() => {
          api.get(`/websites/${selectedSite.id}/keyword-strategy/`)
            .then((r) => {
              setOverview(r.data);
              if (r.data.total_action_plans > 0) {
                clearInterval(poll);
                setAnalyzing(false);
                fetchData();
              }
            })
            .catch(() => {});
        }, 5000);
        setTimeout(() => { clearInterval(poll); setAnalyzing(false); fetchData(); }, 120000);
      })
      .catch(() => setAnalyzing(false));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="animate-spin text-copper" size={20} />
        <span className="ml-2 text-gray-400 text-sm">Loading strategy...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {
              label: "High Priority",
              count: overview.priority_stats?.high?.count || 0,
              sub: overview.priority_stats?.high?.gap ? `Gap: ${overview.priority_stats.high.gap}` : null,
              color: "text-red-400",
              filterKey: "high",
            },
            {
              label: "Medium Priority",
              count: overview.priority_stats?.medium?.count || 0,
              sub: overview.priority_stats?.medium?.gap ? `Gap: ${overview.priority_stats.medium.gap}` : null,
              color: "text-amber-400",
              filterKey: "medium",
            },
            {
              label: "Striking Distance",
              count: overview.striking_distance_count || 0,
              sub: "Positions 11-20",
              color: "text-blue-400",
              filterKey: "striking",
            },
            {
              label: "No Landing Page",
              count: overview.no_landing_page_count || 0,
              sub: "Priority keywords",
              color: overview.no_landing_page_count > 0 ? "text-red-400" : "text-green-400",
              filterKey: "no_page",
            },
          ].map(({ label, count, sub, color, filterKey }) => {
            const isActive = cardFilter === filterKey;
            return (
              <div
                key={label}
                onClick={() => setCardFilter(isActive ? null : filterKey)}
                className={`bg-dark-300 border rounded-xl p-4 cursor-pointer transition-all ${
                  isActive
                    ? "border-copper bg-copper/5 shadow-[0_0_12px_rgba(184,115,51,0.2)]"
                    : "border-dark-50 hover:border-copper/40 hover:shadow-[0_0_12px_rgba(184,115,51,0.1)]"
                }`}
              >
                <div className="text-xs text-gray-500 mb-1">{label}</div>
                <div className={`text-2xl font-mono font-bold ${color}`}>{count}</div>
                {sub && <div className="text-xs text-gray-600 mt-1">{sub}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          {overview && (
            <span className="text-sm text-gray-400">
              Avg Readiness: <span className={`font-mono font-bold ${readinessColor(overview.avg_readiness)}`}>
                {overview.avg_readiness}/100
              </span>
              <span className="text-gray-600 ml-2">({overview.total_action_plans} plans)</span>
            </span>
          )}
          {groups.length > 0 && (
            <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
              className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-copper/50">
              <option value="all">All Groups</option>
              {groups.map((g) => (
                <option key={g.group} value={g.group}>{g.group} ({g.count})</option>
              ))}
            </select>
          )}
          {/* Bulk actions */}
          {selected.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{selected.size} selected</span>
              <button onClick={handleBulkCreatePages}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-green-900/20 text-green-400 rounded-lg hover:bg-green-900/30 transition-colors">
                <Plus size={10} /> Create Pages
              </button>
              <button onClick={handleBulkGeneratePlans}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 transition-colors">
                <Zap size={10} /> Generate Plans
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-900/30 text-green-400 border border-green-800/30 rounded-lg text-sm font-medium hover:bg-green-900/40 transition-colors">
            <Plus size={14} /> Add Strategy Keyword
          </button>
          <button onClick={handleAnalyze} disabled={analyzing}
            className="flex items-center gap-2 px-4 py-2 bg-copper text-dark rounded-lg text-sm font-medium hover:bg-copper/90 transition-colors disabled:opacity-50">
            {analyzing ? <Loader size={14} className="animate-spin" /> : <Zap size={14} />}
            {analyzing ? "Analyzing..." : "Generate Plans"}
          </button>
        </div>
      </div>

      {/* Strategy table */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-dark-50">
                <th className="py-2.5 px-2">
                  <input type="checkbox" onChange={selectAll}
                    checked={filteredGaps.length > 0 && selected.size === filteredGaps.length}
                    className="accent-copper" />
                </th>
                <th className="text-left py-2.5 px-3 font-medium">Keyword</th>
                <th className="text-left py-2.5 px-2 font-medium">Current<span className="block text-[9px] font-normal text-gray-500">Source: GSC 28d</span></th>
                <th className="text-left py-2.5 px-2 font-medium">Target</th>
                <th className="text-left py-2.5 px-2 font-medium">Gap</th>
                <th className="text-left py-2.5 px-2 font-medium">Page?</th>
                <th className="text-left py-2.5 px-2 font-medium">Content</th>
                <th className="text-left py-2.5 px-2 font-medium">On-Page</th>
                <th className="text-left py-2.5 px-2 font-medium">Authority</th>
                <th className="text-left py-2.5 px-2 font-medium">Ready</th>
                <th className="py-2.5 px-2 font-medium text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGaps.map((item) => {
                const plan = item.action_plan;
                return (
                  <tr key={item.id} className={`border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors ${selected.has(item.id) ? "bg-copper/5" : ""}`}>
                    <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(item.id)}
                        onChange={() => toggleSelect(item.id)} className="accent-copper" />
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.priority === "high" ? "bg-red-400" : item.priority === "medium" ? "bg-amber-400" : "bg-green-400"}`} />
                        <span className="font-mono text-gray-200 text-xs">{item.keyword}</span>
                      </div>
                      {item.keyword_group && <span className="text-[10px] text-gray-600 ml-3.5">{item.keyword_group}</span>}
                    </td>
                    <td className="py-2 px-2 font-mono text-xs">{item.current_position?.toFixed(1) ?? "—"}</td>
                    <td className="py-2 px-2 font-mono text-xs text-copper">{item.target_position ?? "—"}</td>
                    <td className="py-2 px-2 font-mono text-xs">
                      {item.gap != null ? (
                        <span className="text-red-400">-{Math.abs(item.gap).toFixed(0)}</span>
                      ) : "—"}
                    </td>
                    <td className="py-2 px-2 text-xs">
                      {plan ? (
                        plan.has_landing_page ? <CheckCircle size={14} className="text-green-400" /> : <XCircle size={14} className="text-red-400" />
                      ) : "—"}
                    </td>
                    <td className="py-2 px-2">
                      {plan ? <span className={`text-xs font-mono ${readinessColor(plan.content_score)}`}>{plan.content_score}</span> : "—"}
                    </td>
                    <td className="py-2 px-2">
                      {plan ? <span className={`text-xs font-mono ${readinessColor(plan.on_page_score)}`}>{plan.on_page_score}</span> : "—"}
                    </td>
                    <td className="py-2 px-2">
                      {plan ? <span className={`text-xs font-mono ${readinessColor(plan.authority_score)}`}>{plan.authority_score}</span> : "—"}
                    </td>
                    <td className="py-2 px-2">
                      {plan ? (
                        <span className={`text-xs font-mono font-bold ${readinessColor(plan.overall_readiness)}`}>
                          {plan.overall_readiness}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex flex-wrap gap-1">
                        {plan && !plan.has_landing_page && (
                          <button onClick={() => { navigate(buildPageGeneratorUrl(item.keyword)); }}
                            className="flex items-center gap-0.5 px-2 py-1 text-[10px] bg-green-900/20 text-green-400 rounded hover:bg-green-900/30 transition-colors"
                            title="Create a landing page for this keyword">
                            <Plus size={10} /> Page
                          </button>
                        )}
                        {plan && plan.on_page_score < 80 && (
                          <button onClick={() => setPlanKeyword({ ...item, id: item.id })}
                            className="flex items-center gap-0.5 px-2 py-1 text-[10px] bg-amber-900/20 text-amber-400 rounded hover:bg-amber-900/30 transition-colors"
                            title="Fix on-page SEO issues">
                            <Zap size={10} /> Fix SEO
                          </button>
                        )}
                        {plan && (
                          <button onClick={() => { navigate(`/backlinks`); }}
                            className="flex items-center gap-0.5 px-2 py-1 text-[10px] bg-blue-900/20 text-blue-400 rounded hover:bg-blue-900/30 transition-colors"
                            title="Build backlinks for this keyword">
                            <Link2 size={10} /> Links
                          </button>
                        )}
                        {plan && plan.content_score < 80 && (
                          <button onClick={() => { navigate(`/content/new?keyword=${encodeURIComponent(item.keyword)}`); }}
                            className="flex items-center gap-0.5 px-2 py-1 text-[10px] bg-purple-900/20 text-purple-400 rounded hover:bg-purple-900/30 transition-colors"
                            title="Add supporting content for this keyword">
                            <FileText size={10} /> Content
                          </button>
                        )}
                        <button onClick={() => setPlanKeyword({ ...item, id: item.id })}
                          className="text-[10px] text-copper hover:underline px-1 py-1"
                          title="View full action plan">
                          Plan
                        </button>
                        {!plan && (
                          <span className="text-[10px] text-gray-600 py-1">No plan</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filteredGaps.length && (
                <tr>
                  <td colSpan={11} className="py-12 text-center text-gray-500 text-sm">
                    No priority keywords set. Go to the Keywords tab and set priorities, then come back to generate action plans.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {planKeyword && (
        <ActionPlanModal keyword={planKeyword} selectedSite={selectedSite} onClose={() => setPlanKeyword(null)} />
      )}
      {showAddModal && (
        <AddStrategyKeywordModal
          selectedSite={selectedSite}
          groups={groups}
          onClose={() => setShowAddModal(false)}
          onSaved={fetchData}
        />
      )}
    </div>
  );
}

/* ============== MAIN COMPONENT ============== */
export default function KeywordTable({ selectedSite }) {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [trendFilter, setTrendFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [sortField, setSortField] = useState("impressions");
  const [sortDir, setSortDir] = useState("desc");
  const [detailKeyword, setDetailKeyword] = useState(null);
  const [priorityModal, setPriorityModal] = useState(null);
  const [activeTab, setActiveTab] = useState("keywords");
  const [selected, setSelected] = useState(new Set());
  const [bulkPriority, setBulkPriority] = useState(null);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    Promise.all([
      api.get(`/websites/${selectedSite.id}/keywords/?page_size=500&include_history=1`),
      api.get(`/websites/${selectedSite.id}/keyword-groups/`),
    ])
      .then(([kwRes, grRes]) => {
        setKeywords(kwRes.data.results || kwRes.data);
        setGroups(grRes.data || []);
      })
      .catch(() => { setKeywords([]); setGroups([]); })
      .finally(() => setLoading(false));
  }, [selectedSite]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    let data = [...keywords];
    if (search) {
      data = data.filter((k) => k.keyword.toLowerCase().includes(search.toLowerCase()));
    }
    if (trendFilter !== "all") {
      data = data.filter((k) => k.trend === trendFilter);
    }
    if (priorityFilter !== "all") {
      if (priorityFilter === "striking") {
        data = data.filter((k) => k.current_position >= 11 && k.current_position <= 20);
      } else {
        data = data.filter((k) => k.priority === priorityFilter);
      }
    }
    if (groupFilter !== "all") {
      data = data.filter((k) => k.keyword_group === groupFilter);
    }
    data.sort((a, b) => {
      const aVal = a[sortField] ?? Infinity;
      const bVal = b[sortField] ?? Infinity;
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return data;
  }, [keywords, search, trendFilter, priorityFilter, groupFilter, sortField, sortDir]);

  const handlePriorityChange = (kwId, newPriority) => {
    api.patch(`/websites/${selectedSite.id}/keywords/${kwId}/priority/`, { priority: newPriority })
      .then((res) => {
        setKeywords((prev) => prev.map((k) => k.id === kwId ? { ...k, ...res.data } : k));
      })
      .catch(() => {});
  };

  const handleBulkPriority = (priority) => {
    if (selected.size === 0) return;
    api.post(`/websites/${selectedSite.id}/keywords/bulk-priority/`, {
      keyword_ids: [...selected],
      priority,
    })
      .then(() => {
        setKeywords((prev) => prev.map((k) => selected.has(k.id) ? { ...k, priority } : k));
        setSelected(new Set());
        setBulkPriority(null);
      })
      .catch(() => {});
  };

  const handleKeywordSaved = (updated) => {
    setKeywords((prev) => prev.map((k) => k.id === updated.id ? updated : k));
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((k) => k.id)));
    }
  };

  const SortHeader = ({ field, children }) => (
    <th
      className="text-left py-2.5 px-3 font-medium cursor-pointer hover:text-copper transition-colors select-none"
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        {sortField === field && (sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </span>
    </th>
  );

  if (!selectedSite) {
    return <div className="text-gray-500 text-center py-20">Select a website to view keywords</div>;
  }

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold font-mono">
          <span className="text-copper">Keyword</span> Strategy
        </h1>
      </div>

      {/* Tab navigation */}
      <div className="flex bg-dark-300 rounded-lg border border-dark-50 overflow-hidden w-fit">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key ? "bg-copper/20 text-copper" : "text-gray-400 hover:text-white"
              }`}>
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Strategy tab */}
      {activeTab === "strategy" && (
        <StrategyTab
          selectedSite={selectedSite}
          keywords={keywords}
          onKeywordUpdate={handleKeywordSaved}
        />
      )}

      {/* Keywords tab */}
      {activeTab === "keywords" && (
        <>
          {/* Filters */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input type="text" placeholder="Search keywords..." value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-dark-300 border border-dark-50 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-copper/50" />
              </div>
              {/* Priority filter */}
              <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}
                className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50">
                <option value="all">All Priorities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                <option value="monitor">Monitor</option>
                <option value="ignore">Ignore</option>
                <option value="striking">Striking Distance (11-20)</option>
              </select>
              {/* Group filter */}
              {groups.length > 0 && (
                <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
                  className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50">
                  <option value="all">All Groups</option>
                  {groups.map((g) => (
                    <option key={g.group} value={g.group}>{g.group} ({g.count})</option>
                  ))}
                </select>
              )}
              {/* Trend filter */}
              <select value={trendFilter} onChange={(e) => setTrendFilter(e.target.value)}
                className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50">
                <option value="all">All Trends</option>
                <option value="rising">Rising</option>
                <option value="falling">Falling</option>
                <option value="stable">Stable</option>
                <option value="new">New</option>
              </select>
            </div>

            {/* Bulk actions */}
            {selected.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{selected.size} selected</span>
                <div className="relative">
                  <button onClick={() => setBulkPriority(bulkPriority ? null : "show")}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 transition-colors">
                    Set Priority <ChevronDown size={10} />
                  </button>
                  {bulkPriority && (
                    <div className="absolute right-0 z-50 mt-1 w-36 bg-dark-300 border border-dark-50 rounded-lg shadow-xl py-1">
                      {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                        <button key={key} onClick={() => handleBulkPriority(key)}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-dark-50/50 ${config.color}`}>
                          {config.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-500 animate-pulse">Loading keywords...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 border-b border-dark-50">
                      <th className="py-2.5 px-3">
                        <input type="checkbox" onChange={selectAll}
                          checked={filtered.length > 0 && selected.size === filtered.length}
                          className="accent-copper" />
                      </th>
                      <th className="text-left py-2.5 px-3 font-medium">Priority</th>
                      <SortHeader field="keyword">Keyword</SortHeader>
                      <SortHeader field="current_position">Position</SortHeader>
                      <th className="text-left py-2.5 px-3 font-medium">Change</th>
                      <SortHeader field="impressions">Impressions</SortHeader>
                      <SortHeader field="clicks">Clicks</SortHeader>
                      <SortHeader field="ctr">CTR</SortHeader>
                      <th className="text-left py-2.5 px-3 font-medium">Trend</th>
                      <th className="py-2.5 px-3 font-medium">Sparkline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((kw) => {
                      const lowConf = kw.impressions < LOW_CONFIDENCE_THRESHOLD;
                      return (
                        <tr key={kw.id}
                          className={`border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors ${lowConf ? "opacity-45" : ""} ${selected.has(kw.id) ? "bg-copper/5" : ""}`}
                          title={lowConf ? "Low confidence — fewer than 5 impressions" : undefined}>
                          <td className="py-2.5 px-3" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={selected.has(kw.id)}
                              onChange={() => toggleSelect(kw.id)} className="accent-copper" />
                          </td>
                          <td className="py-2.5 px-3" onClick={(e) => e.stopPropagation()}>
                            <PriorityBadge
                              priority={kw.priority}
                              onClick={() => setPriorityModal(kw)}
                            />
                          </td>
                          <td className="py-2.5 px-3 font-mono text-gray-200 hover:text-copper transition-colors cursor-pointer"
                            onClick={() => setDetailKeyword(kw.keyword)}>
                            <span className="flex items-center gap-1.5">
                              {kw.keyword}
                              {lowConf && <AlertCircle size={12} className="text-gray-500 flex-shrink-0" />}
                            </span>
                            {kw.target_position && (
                              <span className="text-[10px] text-gray-600 block">
                                Target: #{kw.target_position}
                                {kw.keyword_group && ` · ${kw.keyword_group}`}
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 font-mono">{kw.current_position?.toFixed(1) ?? "—"}</td>
                          <td className="py-2.5 px-3">
                            {kw.position_change > 0 ? (
                              <span className="text-green-400 font-mono flex items-center gap-1">
                                <ArrowUp size={12} />+{kw.position_change.toFixed(1)}
                              </span>
                            ) : kw.position_change < 0 ? (
                              <span className="text-red-400 font-mono flex items-center gap-1">
                                <ArrowDown size={12} />{kw.position_change.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-gray-400">{kw.impressions.toLocaleString()}</td>
                          <td className="py-2.5 px-3 font-mono text-gray-400">{kw.clicks.toLocaleString()}</td>
                          <td className="py-2.5 px-3 font-mono text-gray-400">{(kw.ctr * 100).toFixed(1)}%</td>
                          <td className="py-2.5 px-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              kw.trend === "rising" ? "bg-green-900/30 text-green-400"
                                : kw.trend === "falling" ? "bg-red-900/30 text-red-400"
                                : kw.trend === "new" ? "bg-blue-900/30 text-blue-400"
                                : "bg-gray-800 text-gray-400"
                            }`}>
                              {kw.trend}
                            </span>
                            {kw.source === "gsc" && (
                              <span className="ml-1 text-[9px] bg-blue-900/30 text-blue-400 px-1 py-0.5 rounded">GSC</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 flex justify-center">
                            <KeywordSparkline data={kw.history || []} />
                          </td>
                        </tr>
                      );
                    })}
                    {!filtered.length && (
                      <tr>
                        <td colSpan={10} className="py-12 text-center text-gray-500">
                          {keywords.length ? "No keywords match your filters" : "No keywords tracked yet"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {detailKeyword && (
        <KeywordDetailModal keyword={detailKeyword} selectedSite={selectedSite} onClose={() => setDetailKeyword(null)} />
      )}
      {priorityModal && (
        <SetPriorityModal keyword={priorityModal} selectedSite={selectedSite}
          onClose={() => setPriorityModal(null)} onSaved={handleKeywordSaved} />
      )}
    </div>
  );
}
